import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

// ─── LOGO COMPONENT ───────────────────────────────────
function VKLogo({ height = 38 }) {
    return (
        <img
            src="/images/logo_2.png"
            alt="Vegas Karts"
            style={{
                height: `${height}px`,
                width: "auto",
                objectFit: "contain",
                display: "block",
                mixBlendMode: "lighten",
                filter: "drop-shadow(0 0 6px rgba(255,56,0,0.35))",
                marginLeft: "20px"
            }}
        />
    );
}

// ─── BOOT OVERLAY ─────────────────────────────────────
export function BootOverlay({ onDone }) {
    const canvasRef = useRef(null);
    const [lines, setLines] = useState(["", "", "", "", ""]);
    const [progress, setProgress] = useState(0);

    const BOOT_LINES = [
        "> VEGAS KARTS OS v1.0 // INITIALIZING...",
        "> DRIVER DATABASE: ONLINE",
        "> MISSION REGISTRY: ACTIVE [4 MISSIONS LOADED]",
        "> STREET GRID: LAS VEGAS, NV // LOCKED IN",
        "> SYSTEM: GO. DEPLOYING WINTER 2026.",
    ];

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
        let scanY = 0;
        let animId;

        function draw() {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            for (let i = 0; i < 180; i++) {
                ctx.fillStyle = `rgba(255,56,0,${Math.random() * 0.35})`;
                ctx.fillRect(Math.random() * canvas.width, Math.random() * canvas.height, Math.random() * 2, Math.random() * 2);
            }
            for (let i = 0; i < 60; i++) {
                ctx.fillStyle = `rgba(0,212,255,${Math.random() * 0.15})`;
                ctx.fillRect(Math.random() * canvas.width, Math.random() * canvas.height, Math.random() * 1.5, Math.random() * 1.5);
            }
            ctx.fillStyle = "rgba(255,56,0,0.07)";
            ctx.fillRect(0, scanY, canvas.width, 4);
            scanY = (scanY + 5) % canvas.height;
            ctx.strokeStyle = "rgba(255,56,0,0.035)";
            ctx.lineWidth = 1;
            for (let x = 0; x < canvas.width; x += 60) {
                ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, canvas.height); ctx.stroke();
            }
            for (let y = 0; y < canvas.height; y += 60) {
                ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(canvas.width, y); ctx.stroke();
            }
            animId = requestAnimationFrame(draw);
        }
        draw();
        return () => cancelAnimationFrame(animId);
    }, []);

    useEffect(() => {
        const delays = [200, 900, 1500, 2000, 2600];
        const timers = BOOT_LINES.map((text, i) => {
            return setTimeout(() => {
                let idx = 0;
                const iv = setInterval(() => {
                    setLines(prev => { const next = [...prev]; next[i] = text.slice(0, idx); return next; });
                    idx++;
                    if (idx > text.length) clearInterval(iv);
                }, 22);
            }, delays[i]);
        });
        const progTimers = [
            setTimeout(() => setProgress(20), 400),
            setTimeout(() => setProgress(45), 1100),
            setTimeout(() => setProgress(65), 1700),
            setTimeout(() => setProgress(82), 2200),
            setTimeout(() => setProgress(100), 2800),
        ];
        const autoEnd = setTimeout(onDone, 3500);
        return () => {
            timers.forEach(clearTimeout);
            progTimers.forEach(clearTimeout);
            clearTimeout(autoEnd);
        };
    }, []);

    return (
        <div className="vk-boot-overlay" id="boot-overlay">
            <canvas ref={canvasRef} className="vk-boot-canvas" />
            <div className="vk-boot-center">
                {/* Logo */}
                <div className="vk-boot-logo-wrap">
                    <VKLogo height={90} />
                    {/* <div className="logo_text">
                        <span className="vegas_text">VEGAS</span>
                        <span className="karts_text">KARTS</span>
                    </div> */}
                </div>

                <div className="vk-boot-text">
                    {lines.map((l, i) => (
                        <div key={i} className="vk-boot-line">{l}</div>
                    ))}
                </div>
                <div className="vk-boot-progress-wrap">
                    <div className="vk-boot-progress-bar">
                        <div className="vk-boot-progress-fill" style={{ width: `${progress}%` }} />
                    </div>
                    <span className="vk-boot-progress-label">{progress}%</span>
                </div>
            </div>
            <button className="vk-boot-skip" onClick={onDone}>SKIP <span>▶</span></button>
        </div>
    );
}

// ─── HUD NAV ──────────────────────────────────────────
const PANEL_LABELS = ["HOME", "ABOUT", "THE KART", "TOURS", "DRIVER", "TROPHIES", "CONTACT", "EVENTS","PRE-ORDER"];

const DRAWER_SECTIONS = [
    {
        heading: "MORE",
        items: [
            { label: "TROPHIES", panel: 5, desc: "Success & Rewards" },
            { label: "CONTACT", panel: 6, desc: "Team & Credits" },
            { label: "EVENTS", panel: 7, desc: "Races & Events" },
        ],
    },
];

export function HudNav({ currentPanel, onNav }) {
    const [menuOpen, setMenuOpen] = useState(false);
    const [hoveredIdx, setHoveredIdx] = useState(-1);
    const hoveredIdxRef = useRef(-1);

    const setHovered = (idx) => {
        hoveredIdxRef.current = idx;
        setHoveredIdx(idx);
    };

    useEffect(() => {
        if (menuOpen) {
            const scrollY = window.scrollY;
            document.body.style.setProperty("--vk-scroll-lock-top", `-${scrollY}px`);
            document.body.classList.add("vk-scroll-locked");
        } else {
            const top = document.body.style.getPropertyValue("--vk-scroll-lock-top");
            document.body.classList.remove("vk-scroll-locked");
            document.body.style.removeProperty("--vk-scroll-lock-top");
            if (top) {
                window.scrollTo(0, parseInt(top) * -1);
            }
        }
        return () => {
            document.body.classList.remove("vk-scroll-locked");
            document.body.style.removeProperty("--vk-scroll-lock-top");
        };
    }, [menuOpen]);

    const close = () => setMenuOpen(false);
    const navigate = (panel) => {
        setHovered(-1);
        close();
        setTimeout(() => {
            onNav(panel);
        }, 50);
    };

    useEffect(() => {
        const clearOnKey = (e) => {
            const navKeys = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"];
            if (navKeys.includes(e.key)) setHovered(-1);
        };
        window.addEventListener("keydown", clearOnKey);
        return () => window.removeEventListener("keydown", clearOnKey);
    }, []);

    useEffect(() => {
        setHovered(-1);
    }, [currentPanel]);

    return (
        <nav className="vk-hud-nav">
            {/* ── Logo ── */}
            <div className="vk-nav-logo">
                <VKLogo height={38} />
                {/* <div className="logo_text">
                    <span className="vegas_text">VEGAS</span>
                    <span className="karts_text">KARTS</span>
                </div> */}
            </div>

            {/* Desktop links */}
            <div className="vk-nav-links">
                {PANEL_LABELS.map((label, i) => {
                    const isCTA = label === "PRE-ORDER";
                    const isActive = i === currentPanel;
                    const isHovered = i === hoveredIdx && !isActive;

                    return (
                        <button
                            key={i}
                            className={[
                                "vk-nav-btn",
                                isActive ? "active" : "",
                                isHovered ? "hovered" : "",
                                isCTA ? "cta" : "",
                            ].filter(Boolean).join(" ")}
                            onClick={() => navigate(i)}
                            onMouseEnter={() => setHovered(i)}
                            onMouseLeave={() => setHovered(-1)}
                        >
                            {isCTA ? (
                                <div className="vk-nav-status">
                                    <span className="vk-status-icon">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                            <path d="M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                            <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                            <path d="M5 21h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                        </svg>
                                    </span>
                                    Download app
                                </div>
                            ) : label}
                        </button>
                    );
                })}
            </div>

            {/* Mobile actions */}
            <div className="vk-nav-mobile-actions">
                <button
                    className="vk-nav-menu-btn"
                    onClick={() => setMenuOpen(true)}
                    aria-label="Open menu"
                >
                    <span className="vk-hamburger-icon">
                        <span /><span /><span />
                    </span>
                </button>
            </div>

            {/* ─── SIDE DRAWER ── */}
            {menuOpen && createPortal(
                <div
                    className="vk-side-overlay"
                    onClick={close}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Navigation menu"
                >
                    <div
                        className="vk-side-drawer"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="vk-drawer-header">
                            <div className="vk-drawer-header-logo">
                                <VKLogo height={28} />
                                {/* <div className="logo_text">
                                    <span className="vegas_text">VEGAS</span>
                                    <span className="karts_text">KARTS</span>
                                </div> */}
                            </div>
                            <button className="vk-drawer-close" onClick={close} aria-label="Close menu">
                                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="16" height="16">
                                    <path d="M2 2l12 12M14 2L2 14" />
                                </svg>
                            </button>
                        </div>

                        <div className="vk-drawer-divider" />

                        <div className="vk-drawer-body">
                            {DRAWER_SECTIONS.map((section) => (
                                <div key={section.heading} className="vk-drawer-section">
                                    <p className="vk-drawer-section-heading">{section.heading}</p>
                                    {section.items.map(({ label, panel, desc }) => (
                                        <button
                                            key={panel}
                                            className={`vk-drawer-item ${currentPanel === panel ? "active" : ""}`}
                                            onClick={() => navigate(panel)}
                                        >
                                            <span className="vk-drawer-item-label">{label}</span>
                                            <span className="vk-drawer-item-desc">{desc}</span>
                                            {currentPanel === panel && (
                                                <span className="vk-drawer-item-indicator" />
                                            )}
                                        </button>
                                    ))}
                                </div>
                            ))}
                        </div>

                        <div className="vk-drawer-footer">
                            <button
                                className="vk-drawer-cta"
                                onClick={() => navigate(8)}
                            >
                                <span className="vk-status-icon">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                        <path d="M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                        <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                        <path d="M5 21h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                </span>
                                Download app
                                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14" style={{ marginLeft: "auto" }}>
                                    <path d="M3 8h10M9 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                            <p className="vk-drawer-footer-note">DEPLOYING WINTER 2026</p>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </nav>
    );
}

// ─── MOBILE BOTTOM NAV ────────────────────────────────
const MOB_NAV_ITEMS = [
    {
        label: "HOME",
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M3 10.5L12 3l9 7.5" />
                <path d="M5 10v10h14V10" />
            </svg>
        ),
    },
    {
        label: "ABOUT",
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3l9 9-9 9-9-9 9-9z" />
                <path d="M12 8l4 4-4 4-4-4 4-4z" />
            </svg>
        ),
    },
    {
        label: "THE KART",
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2l8.66 5v10L12 22l-8.66-5V7L12 2z" />
            </svg>
        ),
    },
    {
        label: "TOURS",
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="9" />
                <circle cx="12" cy="12" r="3" />
            </svg>
        ),
    },
    {
        label: "DRIVER",
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 5v14l11-7z" />
            </svg>
        ),
    },
];

export function MobileBottomNav({ currentPanel, onNav }) {
    return (
        <nav className="vk-mobile-bottom-nav" aria-label="Main navigation">
            {MOB_NAV_ITEMS.map((item, i) => (
                <button
                    key={i}
                    className={`vk-mob-nav-btn${i === currentPanel ? " active" : ""}`}
                    onClick={() => onNav(i)}
                    aria-label={item.label}
                    aria-current={i === currentPanel ? "page" : undefined}
                >
                    <svg className="vk-mob-nav-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        {item.icon}
                    </svg>
                    <span className="vk-mob-nav-label">{item.label}</span>
                </button>
            ))}
        </nav>
    );
}

// ─── PANEL NAV HINT ───────────────────────────────────
export function PanelNav({ prev, next, label, onNav, showNav = true }) {
    if (!showNav) return null;
    return (
        <div className="vk-panel-nav-hint" role="navigation" aria-label="Panel navigation">
            <button
                className="vk-pnh-arrow"
                onClick={() => onNav(prev)}
                aria-label="Previous panel"
            >
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                    <path d="M10 3L5 8l5 5" />
                </svg>
            </button>
            <span className="vk-pnh-label">{label}</span>
            <button
                className="vk-pnh-arrow"
                onClick={() => onNav(next)}
                aria-label="Next panel"
            >
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                    <path d="M6 3l5 5-5 5" />
                </svg>
            </button>
        </div>
    );
}